<template>

  <div class="container-fluid px-4">
    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
          <h1 class="h2">{{ $t('password.change') }}</h1>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <form @submit.prevent="submitChangePassword" novalidate>
          <div class="form-group">
            <label for="currentPassword" class="form-label">{{ $t('password.current') }}</label>
            <div class="password-input-wrapper">
              <input
                :type="showCurrentPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': errors.current_password }"
                id="currentPassword"
                v-model="form.current_password"
                @input="clearCurrentPasswordError"
                :disabled="loading"
              >
              <button
                type="button"
                class="btn-password-toggle"
                @click="showCurrentPassword = !showCurrentPassword"
                tabindex="-1"
              >
                <i :class="showCurrentPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
              </button>
            </div>
            <div v-if="errors.current_password" class="invalid-feedback">
              {{ Array.isArray(errors.current_password) ? errors.current_password[0] : errors.current_password }}
            </div>
          </div>

          <div class="form-group">
            <label for="newPassword" class="form-label">{{ $t('password.new') }}</label>
            <div class="password-input-wrapper">
              <input
                :type="showNewPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': errors.password }"
                id="newPassword"
                v-model="form.password"
                @input="clearPasswordError"
                :disabled="loading"
              >
              <button
                type="button"
                class="btn-password-toggle"
                @click="showNewPassword = !showNewPassword"
                tabindex="-1"
              >
                <i :class="showNewPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
              </button>
            </div>
            <div v-if="errors.password" class="invalid-feedback">
              {{ Array.isArray(errors.password) ? errors.password[0] : errors.password }}
            </div>
          </div>

          <div class="form-group">
            <label for="confirmPassword" class="form-label">{{ $t('password.confirm') }}</label>
            <div class="password-input-wrapper">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': errors.password_confirmation }"
                id="confirmPassword"
                v-model="form.password_confirmation"
                @input="clearConfirmPasswordError"
                :disabled="loading"
              >
              <button
                type="button"
                class="btn-password-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
                tabindex="-1"
              >
                <i :class="showConfirmPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
              </button>
            </div>
            <div v-if="errors.password_confirmation" class="invalid-feedback">
              {{ Array.isArray(errors.password_confirmation) ? errors.password_confirmation[0] : errors.password_confirmation }}
            </div>
          </div>

          <button
            type="submit"
            class="btn btn-primary btn-submit"
            :disabled="loading || !isFormValid"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ $t('password.button') }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { changePassword } from '../../api/user/AuthApi.js';
import { validatePasswordComplexity } from '../../utils/passwordValidator.js';
import useFlashMessage from '../../composables/useFlashMessage.js';
import { colors } from '../../../consts/colors.js';

const router = useRouter();
const { t } = useI18n();
const { successMessage } = useFlashMessage();

const loading = ref(false);
const errors = ref({});
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const form = reactive({
  current_password: '',
  password: '',
  password_confirmation: ''
});

/**
 * Check if form is valid
 */
const isFormValid = computed(() => {
  if (!form.current_password || !form.password || !form.password_confirmation) {
    return false;
  }
  if (form.password !== form.password_confirmation) {
    return false;
  }
  return true;
});

/**
 * Validate form on client side
 */
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  // Current password validation
  if (!form.current_password || form.current_password.trim() === '') {
    errors.value.current_password = t('validation.required.currentPassword');
    isValid = false;
  }

  // New password validation
  if (!form.password || form.password.trim() === '') {
    errors.value.password = t('validation.required.newPassword');
    isValid = false;
  } else {
    // Validate password complexity
    const complexityResult = validatePasswordComplexity(form.password);
    if (!complexityResult.isValid) {
      // Show the first error
      const firstError = complexityResult.errors[0];
      // Convert password_invalid_characters to invalidCharacters
      const errorSuffix = firstError.replace('password_', '').replace(/_([a-z])/g, (match, p1) => p1.toUpperCase());
      errors.value.password = t(`validation.password.${errorSuffix}`);
      isValid = false;
    }
  }

  // Password confirmation validation
  if (!form.password_confirmation || form.password_confirmation.trim() === '') {
    errors.value.password_confirmation = t('validation.required.confirmPassword');
    isValid = false;
  } else if (form.password !== form.password_confirmation) {
    errors.value.password_confirmation = t('validation.mismatch');
    isValid = false;
  }

  return isValid;
};

/**
 * Change password function
 */
const submitChangePassword = async () => {
  if (!validateForm()) {
    // Errors are already set in errors.value and will be displayed inline
    return;
  }

  loading.value = true;
  errors.value = {};

  try {
    await changePassword(
      form.current_password,
      form.password,
      form.password_confirmation
    );
    
    successMessage(t('password.success'));
    
    // Clear form
    form.current_password = '';
    form.password = '';
    form.password_confirmation = '';
    
    // Back to home
    router.push({ name: 'home' });
    
  } catch (error) {
    if (error.status === 422) {
      // Set errors for inline display
      errors.value = error.errors;
    } else {
      // For non-validation errors, set a generic error message
      errors.value = {
        current_password: t('validation.error')
      };
    }
  } finally {
    loading.value = false;
  }
};

/**
 * Functions to clear specific field errors
 */
const clearCurrentPasswordError = () => {
  if (errors.value.current_password) {
    delete errors.value.current_password;
  }
};

const clearPasswordError = () => {
  if (errors.value.password) {
    delete errors.value.password;
  }
};

const clearConfirmPasswordError = () => {
  if (errors.value.password_confirmation) {
    delete errors.value.password_confirmation;
  }
};
</script>


<style lang="scss" scoped>
.form-group {
  margin-bottom: 1.5rem;

  .form-label {
    color: v-bind('colors.textPrimary');
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
  }

  .form-control {
    width: 100%;
    border: 1px solid v-bind('colors.borderMediumGray');
    border-radius: 4px;
    padding: 0.75rem;
    color: v-bind('colors.textPrimary');

    &:focus {
      border-color: v-bind('colors.btnBgColorPrimary');
      box-shadow: 0 0 0 0.2rem rgba(48, 111, 201, 0.25);
      outline: none;
    }

    &.is-invalid {
      border-color: v-bind('colors.error');
    }
  }

  .invalid-feedback {
    color: v-bind('colors.error');
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
  }
}

.password-input-wrapper {
  position: relative;

  .form-control {
    padding-right: 2.5rem;
  }
}

.btn-password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: v-bind('colors.gray[600]');
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: v-bind('colors.gray[700]');
  }

  &:focus {
    outline: none;
  }

  i {
    font-size: 1.1rem;
  }
}

.btn {
  width: 100%;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-submit {
  background-color: v-bind('colors.btnBgColorPrimary');
  border-color: v-bind('colors.btnBgColorPrimary');
  color: v-bind('colors.white');
  margin-bottom: 1rem;
  border: none;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.blue[700]');
  }
}

.btn-back {
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.borderMediumGray');

  &:hover:not(:disabled) {
    background-color: v-bind('colors.gray[50]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.gray[300]');
  }
}
</style>